import { all, put, select, call, cancelled, take, fork, cancel } from 'typed-redux-saga'
import type { SagaIterator, Task } from 'redux-saga'
import {
  GenderAttributeDetails,
  CulturalBackgroundAttributeDetails,
  AdditionalInterestAttributeDetails,
  AppointmentBookingExperience,
  MemberConstraints,
  AvailabilitySlot,
} from '@dialogue/timekeeper'
import moment from 'moment'
import Moment from 'moment-timezone'

import { SagaActionProps } from 'APP/Models/Actions'
import { logger } from 'APP/Lib/Datadog'
import CoreData from 'APP/Services/Coredata'
import { getBio } from 'APP/Services/CMS'
import {
  getAppointmentsApi,
  getBookingsApi,
  getProvidersApi,
  createAppointment,
  fetchAvailabilitiesExists,
} from 'APP/Store/utils/timekeeper'
import {
  fetchAvailabilitiesV2,
  searchAllProvidersAvailabilitiesV3,
  searchOneProviderAvailabilitiesV3,
} from 'APP/Store/utils/silkroad'
import {
  CreateAppointmentPayload,
  FetchAvailabilitiesBaseParams,
  FetchAvailabilitiesV2Params,
  FetchProviderBasedAvailabilitiesV3Params,
} from 'APP/Store/AppointmentBooking/types'
import { flattenDateAvailabilities } from 'APP/Store/AppointmentBooking/utils/availabilityMappers'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import { AvailabilitiesExists } from 'APP/Components/AppointmentBooking/utils/types'
import { BOOKING_FLOW } from 'APP/Components/AppointmentBooking/constants'
import type { RootState } from 'APP/Store/CreateStore'
import { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'
import { selectAccessToken } from 'APP/Redux/LoginRedux'
import { selectPatientProfile } from 'APP/Redux/PatientRedux'

import {
  GetAvailabilitiesParams,
  GetProviderAvailabilitiesParams,
  ProviderDetailsData,
  ProviderAttributes,
  UpdateAppointmentParams,
  UpdateBookingParams,
  Provider,
} from './types'
import { providerBookingActions } from './slice'

// The exists map lives in the time-based store, which the provider flow shares for it.
const selectAvailabilitiesExists = (state: RootState) =>
  state.appointmentBooking.availabilitiesExists

function* isV3Enabled() {
  const { enableAvailabilitiesV3ProviderBasedSearch } = yield* select(selectFeatures)
  return enableAvailabilitiesV3ProviderBasedSearch
}

type FetchAvailabilitiesOptions = {
  includePractitioner: boolean
  includeLicenses: boolean
}

type FetchAvailabilitiesResult = {
  availabilities: AvailabilitySlot[]
  providers: Record<number, Provider>
}

/**
 * Searches all providers' availabilities (V2 or V3)
 */
function* fetchAvailabilitiesV2V3(
  params: FetchAvailabilitiesBaseParams,
  options: FetchAvailabilitiesOptions
): SagaIterator<FetchAvailabilitiesResult> {
  // V3 path
  if (yield* isV3Enabled()) {
    const paramsV3: FetchProviderBasedAvailabilitiesV3Params = {
      ...params,
      timezone: Moment.tz.guess(),
      includeLicenses: options.includeLicenses,
    }
    const { days, providers } = yield* call(searchAllProvidersAvailabilitiesV3, paramsV3)

    return { availabilities: flattenDateAvailabilities(days), providers }
  }

  // V2 fallback
  const paramsV2: FetchAvailabilitiesV2Params = {
    ...params,
    experience: AppointmentBookingExperience.ProviderBased,
  }
  const { availabilities, providers } = yield* call(
    fetchAvailabilitiesV2,
    paramsV2,
    options.includePractitioner,
    options.includeLicenses
  )

  return { availabilities, providers }
}

/**
 * Searches one provider's availabilities drilldown (V2 or V3)
 */
function* fetchProviderAvailabilitiesV2V3(
  providerId: number,
  params: FetchAvailabilitiesBaseParams
): SagaIterator<FetchAvailabilitiesResult> {
  // V3 path
  if (yield* isV3Enabled()) {
    const paramsV3: FetchProviderBasedAvailabilitiesV3Params = {
      ...params,
      timezone: Moment.tz.guess(),
      includeLicenses: true,
    }
    const { days, providers } = yield* call(searchOneProviderAvailabilitiesV3, providerId, paramsV3)

    return { availabilities: flattenDateAvailabilities(days), providers }
  }

  // V2 fallback
  const paramsV2: FetchAvailabilitiesV2Params = {
    ...params,
    experience: AppointmentBookingExperience.ProviderBased,
    providerIds: [providerId],
  }
  const { availabilities, providers } = yield* call(fetchAvailabilitiesV2, paramsV2, true, true)

  return { availabilities, providers }
}

/**
 * Searches the other days the exists map marks as having times, one day at a time, until one
 * returns slots. A failed lookup returns none rather than failing the day's own search.
 */
function* searchNextAvailableDay(searchedDay: string, params: { bookingId: string }) {
  try {
    let availabilitiesExists: AvailabilitiesExists = yield* select(selectAvailabilitiesExists)
    // The exists call may not have landed yet.
    if (!Object.keys(availabilitiesExists).length) {
      availabilitiesExists = yield* call(fetchAvailabilitiesExists, { bookingId: params.bookingId })
    }

    for (const day of Object.keys(availabilitiesExists).sort()) {
      if (day === searchedDay || !availabilitiesExists[day]) continue

      const { availabilities } = yield* call(
        fetchAvailabilitiesV2V3,
        {
          bookingId: params.bookingId,
          dateFrom: moment(day, DATE_FORMAT).startOf('day').format(),
          dateUntil: moment(day, DATE_FORMAT).endOf('day').format(),
        },
        // Only the slots' times are read: skip the practitioner and licenses
        { includePractitioner: false, includeLicenses: false }
      )
      if (availabilities.length) return availabilities
    }
  } catch (e) {
    logger.error('Failed to look up the next available day', {}, e)
  }
  return []
}

const sortByStart = (availabilities: AvailabilitySlot[]) =>
  availabilities.slice().sort((a, b) => moment(a.start_at).valueOf() - moment(b.start_at).valueOf())

export function* getAvailabilities({ payload }: SagaActionProps<GetAvailabilitiesParams>) {
  try {
    const { appointment_type, bookingId, start_at, end_at, provider } = payload

    const startAt = moment(start_at).format()
    const endAt = moment(end_at).format()

    logger.info('getAvailabilities: fetching', {
      bookingId,
      startAt,
      endAt,
      appointmentType: appointment_type,
    })

    // The search reads the member's preferences from the booking, server-side.
    const result = yield* call(
      fetchAvailabilitiesV2V3,
      {
        bookingId,
        dateFrom: startAt,
        dateUntil: endAt,
      },
      {
        includePractitioner: true,
        includeLicenses: true,
      }
    )

    let { availabilities } = result

    const providersMap = result.providers

    // Store provider data in Redux (no CoreData call needed!)
    for (const providerData of Object.values(providersMap)) {
      yield put(providerBookingActions.getProviderSuccess(providerData))
    }

    // A day search that comes back empty looks up the next day with times, for the empty
    // state's "next available appointment". Those slots are stored like the day's own: the
    // provider list only shows the selected day's.
    if (!provider?.length && !availabilities.length) {
      availabilities = yield* searchNextAvailableDay(moment(start_at).format(DATE_FORMAT), {
        bookingId,
      })
    }

    availabilities = sortByStart(availabilities)

    if (yield* cancelled()) {
      return
    }

    yield put(providerBookingActions.getAvailabilitiesSuccess({ availabilities }))
  } catch (e) {
    logger.error('Failed to get availabilities', {}, e)
    yield put(providerBookingActions.getAvailabilitiesFailure({ error: e as Error }))
  }
}

/** Fetches one provider's availabilities over the whole booking window, for their drill-down. */
export function* getProviderAvailabilities({
  payload,
}: SagaActionProps<GetProviderAvailabilitiesParams>) {
  const { bookingId, providerId, start_at, end_at } = payload
  try {
    const { availabilities, providers } = yield* call(fetchProviderAvailabilitiesV2V3, providerId, {
      bookingId,
      dateFrom: moment(start_at).format(),
      dateUntil: moment(end_at).format(),
      experience: BOOKING_FLOW.PROVIDER_BASED,
    })

    for (const providerData of Object.values(providers)) {
      yield put(providerBookingActions.getProviderSuccess(providerData))
    }

    yield put(
      providerBookingActions.getProviderAvailabilitiesSuccess({
        providerId,
        availabilities: sortByStart(availabilities),
      })
    )
  } catch (e) {
    logger.error('Failed to get the provider availabilities', {}, e)
    yield put(
      providerBookingActions.getProviderAvailabilitiesFailure({ providerId, error: e as Error })
    )
  }
}

export function* getBooking({ payload: bookingId }: SagaActionProps<string>) {
  try {
    const api = yield* call(getBookingsApi)
    const response = yield* call(api.getBooking, bookingId)

    yield put(providerBookingActions.getBookingSuccess({ booking: response.data.data }))
  } catch (e) {
    yield put(providerBookingActions.getBookingFailure({ error: e as Error }))
  }
}

export function* updateBooking({ payload }: SagaActionProps<UpdateBookingParams>) {
  try {
    const { bookingId, preferences } = payload

    if (!bookingId) {
      throw new Error('Cannot update booking: no booking id')
    }

    const api = yield* call(getBookingsApi)

    // The member can only update their matching preferences. Location and language
    // are set at booking creation and must not be changed here, so we send only the
    // preference fields rather than echoing the existing member_constraints back.
    const memberConstraints: MemberConstraints = {
      genders: preferences.genders,
      cultural_backgrounds: preferences.cultural_backgrounds,
      additional_interests: preferences.additional_interests,
    }

    const response = yield* call(api.updateBooking, bookingId, {
      member_constraints: memberConstraints,
    })

    yield put(providerBookingActions.updateBookingSuccess({ booking: response.data.data }))
  } catch (e) {
    yield put(providerBookingActions.updateBookingFailure({ error: e as Error }))
  }
}

export function* getProviders({ payload }: SagaActionProps<number[]>) {
  try {
    const accessToken: string = yield select(selectAccessToken)
    const coredataClient = CoreData.create(accessToken)

    const results = yield* all(
      payload.map((id) => call(coredataClient.getPractitioner, id.toString()))
    )

    for (const result of results) {
      const data = result.data?.data
      if (data) {
        const provider = {
          id: Number(data.id),
          givenName: data.givenName || '',
          familyName: data.familyName || '',
          picture: data.picture || undefined,
        } as Provider
        yield put(providerBookingActions.getProviderSuccess(provider))
      }
    }
    yield put(providerBookingActions.getProvidersSuccess())
  } catch (e) {
    yield put(providerBookingActions.getProvidersFailure({ error: e as Error }))
  }
}

export function* getProviderBio({ payload: providerId }: SagaActionProps<number>) {
  try {
    const profile = yield* select(selectPatientProfile)
    const memberId = profile?.id?.toString() || ''
    const response: Awaited<ReturnType<typeof getBio>> = yield* call(
      getBio,
      providerId.toString(),
      undefined,
      memberId
    )
    const extendedProvider = {
      id: providerId,
      bio: response?.bio ?? null,
      jobTitle: response?.job_title,
    }

    yield put(providerBookingActions.getProviderBioSuccess(extendedProvider))
  } catch (_e) {
    yield put(providerBookingActions.getProviderBioFailure({ id: providerId }))
  }
}

function hideIrrelevantValues<Type extends { id: string }>(attribute: Type[]): Type[] {
  return attribute?.filter((a: Type) => a.id !== 'not_disclosed') || []
}

export function* getProvidersAttributes() {
  try {
    const api = yield* call(getProvidersApi)
    const response = yield* call(api.getProvidersAttributes)

    const data = response.data?.data

    const attributes: ProviderAttributes = {
      genders: hideIrrelevantValues<GenderAttributeDetails>(data.genders),
      culturalBackgrounds: hideIrrelevantValues<CulturalBackgroundAttributeDetails>(
        data.cultural_backgrounds
      ),
      additionalInterests: hideIrrelevantValues<AdditionalInterestAttributeDetails>(
        data.additional_interests
      ),
    }
    yield put(providerBookingActions.getProvidersAttributesSuccess({ attributes }))
  } catch (e) {
    yield put(providerBookingActions.getProvidersAttributesFailure({ error: e as Error }))
  }
}

export function* getProviderById({ payload: providerId }: SagaActionProps<number>) {
  try {
    const api = yield* call(getProvidersApi)
    const response = yield* call(api.getById, providerId)
    const data = response.data?.data
    const providerData: ProviderDetailsData = {
      gender: data.gender!,
      culturalBackgrounds: data.cultural_backgrounds || [],
      additionalInterests: data.additional_interests || [],
      otherInterests: Array.isArray(data.other_interests) ? data.other_interests : null,
      id: data.id,
    }

    yield put(providerBookingActions.getProviderByIdSuccess({ providerData }))
  } catch (e) {
    yield put(providerBookingActions.getProviderByIdFailure({ error: e as Error }))
  }
}

export function* getAppointmentTypes() {
  try {
    const api = yield* call(getAppointmentsApi)
    const response = yield* call(
      api.getAppointmentTypes,
      undefined,
      AppointmentBookingExperience.ProviderBased
    )

    const data = response.data?.data

    yield put(providerBookingActions.getAppointmentTypesSuccess(data))
  } catch (e) {
    yield put(providerBookingActions.getAppointmentTypesFailure({ error: e as Error }))
  }
}

export function* bookAppointment({ payload }: SagaActionProps<CreateAppointmentPayload>) {
  try {
    const appointment = yield* call(createAppointment, payload)

    if (appointment?.id) {
      yield put(providerBookingActions.createAppointmentSuccess(appointment))
    } else {
      yield put(
        providerBookingActions.createAppointmentFailure({
          error: new Error('Empty appointment response'),
        })
      )
    }
  } catch (error) {
    yield put(providerBookingActions.createAppointmentFailure({ error: error as Error }))
  }
}

export function* updateAppointment(payload: UpdateAppointmentParams) {
  try {
    const api = yield* call(getAppointmentsApi)
    const { appointment_id, appointment } = payload
    const response = yield* call(api.update, appointment_id, appointment)
    yield put(providerBookingActions.updateAppointmentSuccess(response.data?.data))
  } catch (e) {
    yield put(providerBookingActions.updateAppointmentFailure({ error: e as Error }))
    return { error: (e as any)?.response }
  }
}

export function* watchGetAvailabilities() {
  // Only the latest day search may land, or a slower answer for a day the member has left
  // would replace it.
  yield* takeLatestUntilFlowEnds(providerBookingActions.getAvailabilities.type, getAvailabilities)
}

const END_FLOW_TYPES: string[] = [
  providerBookingActions.createAppointmentSuccess.type,
  providerBookingActions.resetState.type,
  providerBookingActions.resetAppointment.type,
]

/**
 * Keeps the latest fetch per provider, so opening another provider lets the previous one
 * land in its cache. All are cancelled when the booking flow ends.
 */
export function* watchGetProviderAvailabilities() {
  const tasks = new Map<number, Task>()

  while (true) {
    const action = (yield* take([
      providerBookingActions.getProviderAvailabilities.type,
      ...END_FLOW_TYPES,
    ])) as SagaActionProps<GetProviderAvailabilitiesParams>

    if (action.type === providerBookingActions.getProviderAvailabilities.type) {
      const { providerId } = action.payload
      const previous = tasks.get(providerId)
      if (previous?.isRunning()) {
        yield* cancel(previous)
      }
      tasks.set(providerId, yield* fork(getProviderAvailabilities, action))
      continue
    }

    for (const [providerId, task] of tasks) {
      if (task.isRunning()) {
        yield* cancel(task)
        yield put(providerBookingActions.getProviderAvailabilitiesCancelled({ providerId }))
      }
    }
    tasks.clear()
  }
}

/**
 * Keeps only the latest request, and cancels it when the booking flow ends so stale
 * availabilities cannot land after the member moved on or booked.
 *
 * {@see}: https://redux-saga.js.org/docs/advanced/TaskCancellation
 */
function* takeLatestUntilFlowEnds<P>(
  actionType: string,
  worker: (action: SagaActionProps<P>) => Generator
) {
  let task: Task | null = null

  while (true) {
    const action = (yield* take([actionType, ...END_FLOW_TYPES])) as SagaActionProps<P>

    if (task?.isRunning()) {
      yield* cancel(task)
    }
    task = action.type === actionType ? yield* fork(worker, action) : null
  }
}
